import React, { useEffect } from 'react';
import { Sparkles, Loader2, X } from 'lucide-react';
import { SavedPartChoiceGuidance, ClarificationHint, ClarificationHintCandidate, ClarificationHintState, ClarificationQuestion } from './types';
import { positionClassesFromName, catalogDetailEntries, normalizeDetailKey } from '../../../utils/partLabel';
import { isSupersessionDetailKey } from '../../../utils/partSupersession';
import { AiRowTags } from '../../../utils/partTags';

// ── AI hint for a clarification question ──────────────────────────────────────
// Shared by the question card (list-only questions) and the fullscreen map's
// candidates panel (diagram questions). The hint is requested AUTOMATICALLY
// when a question mounts (the result is cached server-side per candidate set)
// and lands in two places:
//   • on every candidate row — `HintCandidateNote`: the model's tags for what
//     sets THAT candidate apart (side, variant, fitment…) plus its one-line
//     "pick this when…";
//   • in the comparison bubble — `HintBubble`/`HintNote`: the intro, the factors
//     and a candidate-by-candidate list. Every reference to a candidate in the
//     bubble is a chip that focuses that candidate (scrolls its row into view,
//     lights its diagram tag) so the reader can jump from the advice to the part.
// The comparison runs long, so it is NOT rendered inline where it would push the
// parts list off the panel — it opens in a popup over whatever surface asked for
// it. Amber like the card's own "cannot determine" banner: it is advice, not a
// catalog fact, and the disclaimer says so in words.

const PRIMARY = '#6866ff';
const AMBER_FG = '#8a5a00';
const AMBER_BG = '#fffbeb';
const AMBER_BORDER = '#fde68a';
const AI_FG = '#4338ca';
const AI_BG = '#f3f1ff';
const AI_BORDER = '#d9d5ff';

type T = (key: string, opts?: any) => string;

/** Focus one candidate from a reference in the hint — implemented by the surface
 *  that owns the rows (scroll + highlight while the comparison stays open). */
export type FocusOption = (optionId: string) => void;

/** The offer of help, not a footnote to it. Solid primary rather than the old
 *  outlined pill: users read a faint bordered "Hint" as decoration and never
 *  pressed it, so the one control that can actually answer "which of these do I
 *  need?" now looks like the action it is. The request itself starts on its own
 *  when the question loads, so the pill mostly reads "Thinking…" first and then
 *  opens the finished comparison. */
export const HintButton: React.FC<{
  t: T;
  loading: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  label?: string;
}> = ({ t, loading, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading}
    title={t('chat.clarification.hintTitle', { defaultValue: 'See what to look for' })}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0,
      padding: '6px 13px', fontSize: '12px', fontWeight: 700, lineHeight: '16px',
      color: '#514d68', backgroundColor: 'transparent', border: '1px solid #dedde5',
      borderRadius: '999px', cursor: loading ? 'default' : 'pointer', fontFamily: 'inherit',
      boxShadow: 'none',
    }}
  >
    {loading
      ? <Loader2 size={13} strokeWidth={2.4} className="animate-spin" aria-hidden />
      : <Sparkles size={13} strokeWidth={2.4} aria-hidden />}
    {loading
      ? t('chat.clarification.hintLoading', { defaultValue: 'Thinking…' })
      : (label ?? t('chat.clarification.hintButton', { defaultValue: 'Help me choose' }))}
  </button>
);

// ── Candidate references ──────────────────────────────────────────────────────

/** How the list names a candidate: the OE number the user actually orders by
 *  is primary; the shared diagram callout is secondary context only. */
const candidateRef = (question: ClarificationQuestion, optionId: string) => {
  const opt = question.options.find(o => o.id === optionId);
  const load = opt?.load?.[0];
  return {
    callout: load?.callout ? String(load.callout) : null,
    partNumber: load?.part_number ? String(load.part_number) : null,
    label: opt?.label ?? optionId,
  };
};

/** A candidate named inline in the hint. With `onFocus` it is a button that
 *  jumps to the candidate; without, the same chip as plain text. */
const RefChip: React.FC<{
  t: T;
  question: ClarificationQuestion;
  optionId: string;
  onFocus?: FocusOption;
  /** Override the visible text (e.g. the part number the sentence already used). */
  text?: string;
}> = ({ t, question, optionId, onFocus, text }) => {
  const ref = candidateRef(question, optionId);
  const shown = text ?? [
    ref.partNumber ? `#${ref.partNumber}` : null,
    ref.callout ? `diagram ${ref.callout}` : null,
  ].filter(Boolean).join(' · ');
  const style: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 4, verticalAlign: 'baseline',
    fontSize: 11.5, fontWeight: 700, lineHeight: '16px', padding: '0 6px', borderRadius: 6,
    color: PRIMARY, background: '#eeedff', border: `1px solid ${onFocus ? '#d6d5ff' : 'transparent'}`,
    fontFamily: 'inherit', cursor: onFocus ? 'pointer' : 'default', whiteSpace: 'nowrap',
    maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
  };
  if (!onFocus) return <span style={style}>{shown || ref.label}</span>;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onFocus(optionId); }}
      title={t('chat.clarification.hintShowCandidate', { defaultValue: 'Show this candidate' })}
      style={style}
    >
      {shown || ref.label}
    </button>
  );
};

/** Turn every OE part number the sentence mentions into a candidate chip. The
 *  model is told to name candidates by part number, so this is what makes the
 *  intro and the factor texts clickable without asking it for markup. Exact
 *  string match on the numbers the question carries, longest first so a number
 *  that is a prefix of another never steals its match. */
const linkifyRefs = (
  text: string,
  question: ClarificationQuestion,
  t: T,
  onFocus?: FocusOption,
): React.ReactNode => {
  if (!text) return null;
  const numbers = question.options
    .map(o => ({ id: o.id, pn: o.load?.[0]?.part_number ? String(o.load[0].part_number) : '' }))
    .filter(x => x.pn.length >= 4)
    .sort((a, b) => b.pn.length - a.pn.length);
  if (!numbers.length) return text;
  const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`(${numbers.map(n => escape(n.pn)).join('|')})`, 'g');
  const parts = text.split(re);
  if (parts.length === 1) return text;
  return parts.map((chunk, i) => {
    const hit = numbers.find(n => n.pn === chunk);
    return hit
      ? <RefChip key={i} t={t} question={question} optionId={hit.id} onFocus={onFocus} text={chunk} />
      : <React.Fragment key={i}>{chunk}</React.Fragment>;
  });
};

// ── Per-row enrichment ────────────────────────────────────────────────────────

/** What a row can say about a candidate: the backend's entry, completed on the
 *  client when it is missing or silent — side/position parsed from the factor
 *  lines that mention the option and from its catalog name (the same left/
 *  right, front/rear tokens the evidence chips use). A left/right pair thus
 *  always wears its side, whether or not the model bothered to tag it. */
export const resolveCandidate = (
  hint: ClarificationHint,
  question: ClarificationQuestion,
  optionId: string,
): (ClarificationHintCandidate & { side: 'left' | 'right' | null; position: 'front' | 'rear' | null }) | null => {
  const entry = hint.candidates?.find(c => c.option_id === optionId);
  const opt = question.options.find(o => o.id === optionId);
  const differingFactors = (hint.factors || []).filter(f => {
    if (!f.title || !f.per_option?.length) return false;
    const values = question.options.map(option =>
      String(f.per_option?.find(item => item.option_id === option.id)?.text || '').trim().toLocaleLowerCase(),
    );
    return new Set(values).size > 1;
  });
  // A characteristic the evidence is silent about is dropped from the row: an
  // empty value ("Finish: Not specified") tells the user nothing and crowds out
  // the differences that do.
  const lines = differingFactors
    .map(f => ({
      title: f.title,
      text: String(f.per_option?.find(po => po.option_id === optionId)?.text || '').trim(),
    }))
    .filter(line => line.text);
  // Rows share the comparison dimensions in the order the backend ranked them,
  // which makes them scannable: "Finish: Painted" beside "Finish: Unpainted",
  // rather than unrelated free-form labels.
  const comparisonTags = lines.map(line => `${line.title}: ${line.text}`);
  // A model tag every candidate wears ("Equipment: 5DOOR" on all rows) tells
  // nothing apart. The backend strips these now; older cached hints still
  // carry them, so the same rule runs here.
  const others = (hint.candidates || []).filter(c => c.option_id !== optionId);
  // Same rule among the parts at the SAME callout: three piston/liner kits all
  // tagged "Piston with liner" tell nothing apart, even though the rings and
  // pins the hint also compared don't wear that tag.
  const calloutsOf = (id: string) => new Set((question.options.find(o => o.id === id)?.load || []).map(l => String(l.callout || '')).filter(Boolean));
  const ownCallouts = calloutsOf(optionId);
  const peers = others.filter(c => Array.from(calloutsOf(c.option_id)).some(k => ownCallouts.has(k)));
  const hasTag = (c: ClarificationHintCandidate, tag: string) => (c.tags || []).some(t => t.toLocaleLowerCase() === tag.toLocaleLowerCase());
  const shared = new Set(
    (entry?.tags || [])
      .filter(tag => (others.length > 0 && others.every(c => hasTag(c, tag))) || (peers.length > 0 && peers.every(c => hasTag(c, tag))))
      .map(tag => tag.toLocaleLowerCase()),
  );
  const modelTags = (entry?.tags || []).filter(tag => !shared.has(tag.toLocaleLowerCase()));
  const tags = comparisonTags.length ? comparisonTags : modelTags;
  const summary = entry?.summary || (!comparisonTags.length && lines[0]
    ? `${lines[0].title ? `${lines[0].title}: ` : ''}${lines[0].text}`
    : '');
  const guess = [lines.map(l => l.text).join(' '), opt?.label || '', ...(opt?.load || []).map(l => l.part_name || '')]
    .reduce<{ side: 'left' | 'right' | null; position: 'front' | 'rear' | null }>((acc, text) => {
      const parsed = positionClassesFromName(text);
      return { side: acc.side || parsed.side, position: acc.position || parsed.position };
    }, { side: null, position: null });
  const side = entry?.side === 'left' || entry?.side === 'right' ? entry.side : guess.side;
  const position = entry?.position === 'front' || entry?.position === 'rear' ? entry.position : guess.position;
  if (!tags.length && !summary && !side && !position) return null;
  return { option_id: optionId, tags, summary, side, position, score_adjustment: entry?.score_adjustment };
};

/** Keep both fresh and previously cached guidance brief enough to scan. */
const conciseChoiceText = (value: string): string => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (text.length <= 360) return text;
  const shortened = text.slice(0, 357).replace(/\s+\S*$/, '').trim();
  return `${shortened}…`;
};

// ── Row guidance filters ──────────────────────────────────────────────────────
const foldText = (s: string): string => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** A tag that only repeats the part's own name ("Pistón con camisa" on
 *  "Pistón con camisa de cilindro") says nothing about which one to pick. */
export const isNameEcho = (value: string, names: string[]): boolean => {
  const v = foldText(value);
  if (!v) return true;
  return names.some(n => { const f = foldText(n); return !!f && f.includes(v); });
};

/** "Choose this if you need the set with reference A 457 030 22 37" restates
 *  the row's own number — circular, not a reason. */
export const isCircularInsight = (summary: string, partNumbers: string[]): boolean => {
  const c = foldText(summary).replace(/ /g, '');
  return partNumbers.some(pn => { const p = foldText(pn).replace(/ /g, ''); return p.length >= 6 && c.includes(p); });
};

type RowFilter = { exclude?: string[]; names?: string[] };
/** What a diagram part row should actually show from the hint: the
 *  distinguishing tags minus name echoes and values the row already states,
 *  and the "choose when" condition unless it is circular. */
export const computeRowGuidance = (guidance: SavedPartChoiceGuidance, t: T, filter: RowFilter = {}) => {
  const { candidate } = guidance;
  const cap = (x: string) => x.charAt(0).toUpperCase() + x.slice(1);
  const attr = (key: string, fallback: string) => cap(t(key, { defaultValue: fallback }));
  const placed = [
    candidate.side ? { label: attr('chat.clarification.attrSide', 'side'), value: t(`chat.position.${candidate.side}`, { defaultValue: cap(candidate.side) }) } : null,
    candidate.position ? { label: attr('chat.clarification.attrPosition', 'position'), value: t(`chat.position.${candidate.position}`, { defaultValue: cap(candidate.position) }) } : null,
  ].filter((x): x is { label: string; value: string } => !!x);
  const said = new Set([...placed.map(p => p.value), ...(filter.exclude || [])].map(v => v.trim().toLowerCase()));
  const names = [guidance.part_name, ...(filter.names || []), ...(guidance.part_refs || []).map(r => (r as any).part_name || '')].filter(Boolean) as string[];
  const tags = [
    ...placed,
    ...(candidate.tags || []).map(splitDifferenceTag)
      .filter(tag => tag.value && !said.has(tag.value.toLowerCase()) && !isNameEcho(tag.value, names)),
  ];
  const numbers = (guidance.part_refs || []).map(r => (r as any).part_number || '').filter(Boolean) as string[];
  const summary = candidate.summary || '';
  const insight = summary && !isCircularInsight(summary, numbers) ? insightCondition(summary) : null;
  return { tags, insight };
};

/** The AI's reading of ONE candidate as the tags its part row wears — the
 *  SAME in the question viewer and the search-results viewer, so a part reads
 *  identically wherever it is drawn: its side / position become position
 *  tags; every other distinguishing value a feature tag ("Finish: Painted"),
 *  minus what the card already shows (``filter.exclude``), what merely echoes
 *  a name, and the factor the answers were built from (``skipLabel``). */
export const aiRowTagsOf = (guidance: SavedPartChoiceGuidance, t: T, filter: RowFilter = {}, skipLabel = ''): AiRowTags => {
  const { candidate } = guidance;
  const { tags } = computeRowGuidance(guidance, t, filter);
  const placed = (candidate.side ? 1 : 0) + (candidate.position ? 1 : 0);
  const skip = skipLabel.trim().toLowerCase();
  const features = tags.slice(placed)
    .filter(tg => !skip || tg.label.trim().toLowerCase() !== skip)
    .map(tg => (tg.label ? `${tg.label}: ${tg.value}` : tg.value));
  return { features, side: candidate.side ?? null, position: candidate.position ?? null };
};

/** Does the hint say anything that sets THIS row apart (after the filters)? */
export const rowHasHintDifference = (
  state: ClarificationHintState | undefined, question: ClarificationQuestion, optionId: string, t: T, filter: RowFilter = {},
): boolean => {
  if (!state || state.status !== 'ready') return false;
  const candidate = resolveCandidate(state.hint, question, optionId);
  if (!candidate) return false;
  const option = question.options.find(o => o.id === optionId);
  const g = computeRowGuidance({ part_name: question.part_label || question.part_name, part_parameters: [], part_refs: option?.load || [], candidate }, t, filter);
  return g.tags.length > 0 || !!g.insight?.condition;
};

/** What the AI says about THIS candidate, pinned to its row: a tiny "AI" heading,
 *  the distinguishing tags as amber chips, and the one-line "pick this when…".
 *  Shows a muted progress line while the hint is still being generated so the
 *  row explains why its tags are late, and nothing at all when the model had
 *  nothing to say about the option. */
export const HintCandidateNote: React.FC<{
  t: T;
  state?: ClarificationHintState;
  /** The question whose options the hint compares (scoped to the diagram for
   *  map questions) — needed to complete a missing entry from the factor lines
   *  and the catalog name. */
  question: ClarificationQuestion;
  optionId: string;
  /** `row` = a part row in the diagram list: tags AND the one-line "choose
   *  this when…" insight, framed as a guidance card, plus an "AI suggests"
   *  mark on the leaning option. Loading is shown once by the list strip,
   *  not on every row. */
  variant?: 'compact' | 'row';
  /** Values the row already states (catalog differences) — not repeated. */
  exclude?: string[];
  /** The row's displayed names (original + translated) — tags echoing them are dropped. */
  names?: string[];
  /** The row shows the tags in its own tag row: keep only "Suggested" and
   *  the "choose when…" line here. */
  hideTags?: boolean;
}> = ({ t, state, question, optionId, variant = 'compact', exclude, names, hideTags }) => {
  if (question.options.find(o => o.id === optionId)?.extra_details?._expert_action) return null;
  if (!state) return null;
  if (state.status === 'loading' && variant === 'row') return null;
  if (state.status === 'loading') {
    return (
      <span style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 3, padding: '3px 0' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: '#9a8a5a' }}>
          <Loader2 size={11} strokeWidth={2.2} className="animate-spin" aria-hidden />
          {t('chat.clarification.hintRowLoading', { defaultValue: 'Checking what to look for…' })}
        </span>
      </span>
    );
  }
  if (state.status !== 'ready') return null;
  const candidate = resolveCandidate(state.hint, question, optionId);
  if (!candidate) return null;
  const option = question.options.find(o => o.id === optionId);
  const guidance = { part_name: question.part_label || question.part_name, part_parameters: question.part_parameters || [], part_refs: option?.load || [], candidate, probability_score: option?.probability_score };
  if (variant === 'row') {
    return <ChoiceGuidanceNote t={t} guidance={guidance} showInsight suggested={state.hint.suggested_option_id === optionId} exclude={exclude} names={names} hideTags={hideTags} />;
  }
  return <ChoiceGuidanceNote t={t} guidance={guidance} />;
};

/** "Choose this when the vehicle is equipped with …" → the condition alone,
 *  so the row reads "When: the vehicle is equipped with …". Non-English
 *  summaries (or ones phrased differently) are shown whole. */
export const insightCondition = (summary: string): { condition: string; stripped: boolean } => {
  const text = conciseChoiceText(summary);
  const m = text.match(/^(?:choose|pick|select)\s+(?:this|it|this one|this part)\s+(?:when|if)\s+(.+)$/i);
  if (!m) return { condition: text, stripped: false };
  return { condition: m[1].replace(/[.\s]+$/, ''), stripped: true };
};

/** Split a distinguishing tag into its dimension and its value ("Finish: Painted"
 *  → label "Finish", value "Painted"). A tag without a label is all value. */
export const splitDifferenceTag = (tag: string): { label: string; value: string } => {
  const text = String(tag || '').replace(/\s+/g, ' ').trim();
  const at = text.indexOf(':');
  if (at <= 0 || at === text.length - 1) return { label: '', value: text };
  return { label: text.slice(0, at).trim(), value: text.slice(at + 1).trim() };
};

/** What sets THIS candidate apart, pinned to its row: a tiny "AI" heading and
 *  one bold line per distinguishing dimension. The value is what the eye must
 *  land on ("Finish: **Painted**"); the model's "pick this when…" sentence is
 *  deliberately not shown — nobody reads a paragraph per row, the difference
 *  is the answer. Renders nothing when there is no difference to show. */
export const ChoiceGuidanceNote: React.FC<{
  t: T;
  guidance?: SavedPartChoiceGuidance | null;
  /** Also show the "choose this when…" sentence (diagram part rows). */
  showInsight?: boolean;
  /** The comparison leans towards this option. */
  suggested?: boolean;
  /** Tag values already shown elsewhere on the row (case-insensitive). */
  exclude?: string[];
  /** Names the row displays; tags that merely repeat them are dropped. */
  names?: string[];
  /** The tags render elsewhere (the diagram row's tag row). */
  hideTags?: boolean;
}> = ({ t, guidance, showInsight = false, suggested = false, exclude, names, hideTags = false }) => {
  if (!guidance) return null;
  const { candidate } = guidance;
  // The side / position the AI read for THIS part leads: the prompt keeps them
  // out of the tags (they go in these fields), so without this two identical
  // panels showed no left/right at all.
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const attr = (key: string, fallback: string) => cap(t(key, { defaultValue: fallback }));
  const placed = [
    candidate.side ? { label: attr('chat.clarification.attrSide', 'side'), value: t(`chat.position.${candidate.side}`, { defaultValue: cap(candidate.side) }) } : null,
    candidate.position ? { label: attr('chat.clarification.attrPosition', 'position'), value: t(`chat.position.${candidate.position}`, { defaultValue: cap(candidate.position) }) } : null,
  ].filter((x): x is { label: string; value: string } => !!x);
  const said = new Set([...placed.map(p => p.value), ...(exclude || [])].map(v => v.trim().toLowerCase()));
  const tags = [
    ...placed,
    ...(candidate.tags || []).map(splitDifferenceTag).filter(tag => tag.value && !said.has(tag.value.toLowerCase())),
  ];
  if (showInsight) {
    const row = computeRowGuidance(guidance, t, { exclude, names });
    const insight = row.insight;
    const rowTags = hideTags ? [] : row.tags;
    // Nothing that sets this row apart → no AI box at all (a box that only
    // repeats the name or the number reads as advice and isn't).
    if (!rowTags.length && !insight?.condition && !(hideTags && suggested)) return null;
    return (
      <span
        data-testid="hint-guidance"
        data-variant="row"
        style={{ display: 'flex', flexDirection: 'column', gap: 5, width: '100%', minWidth: 0, boxSizing: 'border-box', marginTop: 5, padding: '7px 9px', background: AI_BG, border: `1px solid ${suggested ? '#a5a0ff' : AI_BORDER}`, borderRadius: 8 }}
      >
        <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 5, minWidth: 0 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10.5, fontWeight: 700, color: AI_FG, letterSpacing: '0.02em', whiteSpace: 'nowrap', marginRight: 2 }}>
            <Sparkles size={11} aria-hidden />
            {t('chat.clarification.hintRowHeading', { defaultValue: 'AI' })}
          </span>
          {suggested && (
            <span data-testid="hint-suggested" style={{ display: 'inline-flex', alignItems: 'center', fontSize: 10.5, fontWeight: 800, color: '#fff', background: AI_FG, borderRadius: 999, padding: '1px 8px', lineHeight: '16px', whiteSpace: 'nowrap' }}>
              {t('chat.clarification.hintSuggestedBadge', { defaultValue: 'Suggested' })}
            </span>
          )}
          {rowTags.map((tag, index) => (
            <span
              key={`${tag.label}-${tag.value}-${index}`}
              data-testid="hint-difference"
              style={{ display: 'inline-flex', alignItems: 'baseline', gap: 3, fontSize: 11.5, color: AMBER_FG, background: AMBER_BG, border: `1px solid ${AMBER_BORDER}`, borderRadius: 6, padding: '1px 7px', maxWidth: '100%', overflowWrap: 'anywhere', lineHeight: 1.4 }}
            >
              {tag.label && <span style={{ fontWeight: 500 }}>{tag.label}: </span>}
              <strong style={{ fontWeight: 800, color: '#6b4400' }}>{tag.value}</strong>
            </span>
          ))}
        </span>
        {insight?.condition && (
          <span data-testid="hint-insight" style={{ fontSize: 11.5, lineHeight: 1.45, color: '#3f3b56', overflowWrap: 'anywhere' }}>
            {insight.stripped && <strong style={{ fontWeight: 700, color: AI_FG }}>{t('chat.clarification.hintChooseWhen', { defaultValue: 'Choose when' })}: </strong>}
            {insight.condition}
          </span>
        )}
      </span>
    );
  }
  if (!tags.length) return null;

  return (
    <span
      data-testid="hint-guidance"
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 5, width: '100%', minWidth: 0, boxSizing: 'border-box', marginTop: 4, background: 'transparent' }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10.5, fontWeight: 700, color: AI_FG, letterSpacing: '0.02em', whiteSpace: 'nowrap', marginRight: 2 }}>
        <Sparkles size={11} aria-hidden />
        {t('chat.clarification.hintHeading', { defaultValue: 'AI guidance' })}
      </span>
      {tags.map((tag, index) => (
        <span
          key={`${tag.label}-${tag.value}-${index}`}
          data-testid="hint-difference"
          style={{ display: 'inline-flex', alignItems: 'baseline', gap: 3, fontSize: 11.5, color: AMBER_FG, background: AMBER_BG, border: `1px solid ${AMBER_BORDER}`, borderRadius: 6, padding: '2px 7px', maxWidth: '100%', overflowWrap: 'anywhere', lineHeight: 1.4 }}
        >
          {tag.label && <span style={{ fontWeight: 500 }}>{tag.label}: </span>}
          <strong style={{ fontWeight: 800, color: '#6b4400' }}>{tag.value}</strong>
        </span>
      ))}
    </span>
  );
};

/** Top of the diagram parts list: the comparison, summarised where the choice
 *  is made — how many parts the AI compared, the dimensions they differ on, and
 *  the way into the full side-by-side table. While the hint is still being
 *  written it says so once here (the rows stay quiet). */
export const HintComparisonStrip: React.FC<{
  t: T;
  state?: ClarificationHintState;
  question: ClarificationQuestion;
  onOpen: () => void;
  /** The comparison opens from the list's own "What's different?" button. */
  hideOpen?: boolean;
}> = ({ t, state, question, onOpen, hideOpen }) => {
  if (!state) return null;
  const frame: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, margin: '10px 10px 2px', padding: '8px 10px', borderRadius: 10, background: AI_BG, border: `1px solid ${AI_BORDER}`, fontSize: 12, color: '#3f3b56' };
  if (state.status === 'loading') {
    return (
      <div data-testid="hint-strip" style={frame}>
        <Loader2 size={13} className="animate-spin" color={AI_FG} aria-hidden />
        <span>{t('chat.clarification.hintStripLoading', { defaultValue: 'AI is comparing these parts…' })}</span>
      </div>
    );
  }
  if (state.status !== 'ready') return null;
  const ids = new Set(question.options.map(o => o.id));
  const compared = (state.hint.candidates || []).filter(c => ids.has(c.option_id)).length;
  // The dimensions that actually separate the options (the table's rows).
  const dims = Array.from(new Set(
    question.options.flatMap(o => resolveCandidate(state.hint, question, o.id)?.tags || [])
      .map(tag => splitDifferenceTag(tag).label).filter(Boolean),
  )).slice(0, 3);
  if (compared < 2 && !dims.length) return null;
  return (
    <div data-testid="hint-strip" style={frame}>
      <Sparkles size={14} color={AI_FG} aria-hidden style={{ flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0, lineHeight: 1.4 }}>
        <strong style={{ color: AI_FG }}>
          {t('chat.clarification.hintStripCompared', { defaultValue: 'AI compared {{count}} parts', count: compared })}
        </strong>
        {dims.length > 0 && (
          <> · {t('chat.clarification.hintStripDiffer', { defaultValue: 'they differ by' })} <strong>{dims.join(', ')}</strong></>
        )}
      </span>
      {!hideOpen && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(); }}
          style={{ flexShrink: 0, border: `1px solid ${AI_BORDER}`, background: '#fff', color: AI_FG, fontSize: 11.5, fontWeight: 700, borderRadius: 999, padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
          {t('chat.clarification.hintStripOpen', { defaultValue: 'Compare side by side' })}
        </button>
      )}
    </div>
  );
};

// ── The comparison ────────────────────────────────────────────────────────────

export const HintNote: React.FC<{
  t: T;
  question: ClarificationQuestion;
  state: ClarificationHintState;
  /** Skip the amber box + heading (the bubble supplies its own chrome). */
  bare?: boolean;
  /** Makes every candidate reference a jump to that candidate. */
  onFocusOption?: FocusOption;
  /** Also name what the candidates have in common ("What's different?"). */
  showIdentical?: boolean;
}> = ({ t, question, state, bare, onFocusOption, showIdentical }) => {
  if (state.status === 'loading') return null;
  const ready: ClarificationHint | null = state.status === 'ready' ? state.hint : null;
  // Candidate-by-candidate block, in the order the list shows them (the model's
  // order is not meaningful and the row order is what the user is looking at).
  const candidates = ready
    ? question.options
      .filter(o => !o.extra_details?._expert_action)
      .map(o => resolveCandidate(ready, question, o.id))
      .filter((c): c is NonNullable<ReturnType<typeof resolveCandidate>> => !!c)
    : [];
  const normalizedValue = (value: string | null | undefined) => String(value || '').trim().toLocaleLowerCase();
  const comparisonFactors = ready
    ? (ready.factors || []).filter(factor => {
        if (!factor.title) return false;
        const values = candidates.map(candidate =>
          normalizedValue(factor.per_option?.find(item => item.option_id === candidate.option_id)?.text),
        );
        // A comparison row earns its space only when it separates at least two
        // candidates. Missing values remain meaningful beside a supplied value.
        return new Set(values).size > 1;
      })
    : [];
  const tagFrequency = new Map<string, number>();
  for (const candidate of candidates) {
    for (const tag of Array.from(new Set((candidate.tags || []).map(normalizedValue).filter(Boolean)))) {
      tagFrequency.set(tag, (tagFrequency.get(tag) || 0) + 1);
    }
  }
  // What every compared candidate shares: the comparison's rows whose value
  // is the same for all of them, the catalog fields they all carry with one
  // value, and the name when it is the same. Replacement chains, alternatives
  // and packaging say nothing about the part itself and are left out.
  const identical: Array<{ label: string; value: string }> = (() => {
    if (!showIdentical || !ready || candidates.length < 2) return [];
    const out: Array<{ label: string; value: string }> = [];
    const seen = new Set<string>();
    const add = (label: string, value: string) => {
      const k = `${normalizedValue(label)}::${normalizedValue(value)}`;
      if (!value.trim() || seen.has(k)) return;
      seen.add(k);
      out.push({ label, value: value.trim() });
    };
    const opts = candidates.map(c => question.options.find(o => o.id === c.option_id));
    const names = opts.map(o => String(o?.load?.[0]?.part_name || o?.label || '').trim());
    if (names.every(Boolean) && new Set(names.map(normalizedValue)).size === 1) add(t('chat.clarification.identicalName', { defaultValue: 'Name' }), names[0]);
    for (const f of ready.factors || []) {
      if (!f.title) continue;
      const values = candidates.map(c => String(f.per_option?.find(p => p.option_id === c.option_id)?.text || '').trim());
      if (values.every(Boolean) && new Set(values.map(normalizedValue)).size === 1) add(f.title, values[0]);
    }
    const detailsOnly = (k: string) => { const n = String(k).toLowerCase().replace(/[^a-z0-9]/g, ''); return isSupersessionDetailKey(k) || n.includes('alternat') || n.startsWith('optionalpart') || n.includes('pack'); };
    const fields = opts.map(o => catalogDetailEntries((o?.extra_details || null) as Record<string, unknown> | null).filter(d => !detailsOnly(d.key)));
    for (const d of fields[0] || []) {
      const norm = normalizeDetailKey(d.key);
      const values = fields.map(list => list.find(x => normalizeDetailKey(x.key) === norm)?.value || '');
      if (values.every(Boolean) && new Set(values.map(normalizedValue)).size === 1) add(t(`chat.clarification.detail.${norm}`, { defaultValue: d.label }), values[0]);
    }
    return out;
  })();
  const distinguishingTags = (candidate: (typeof candidates)[number]) =>
    (candidate.tags || []).filter(tag =>
      candidates.length === 1 || (tagFrequency.get(normalizedValue(tag)) || 0) < candidates.length,
    );
  return (
    <div
      role="note"
      style={bare ? { display: 'flex', flexDirection: 'column', gap: '6px' } : {
        display: 'flex', flexDirection: 'column', gap: '6px', flexShrink: 0,
        padding: '8px 10px', borderRadius: '8px',
        backgroundColor: AMBER_BG, border: `1px solid ${AMBER_BORDER}`,
      }}
    >
      {!bare && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10px', fontWeight: 700, color: AMBER_FG, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          <Sparkles size={11} strokeWidth={2.2} aria-hidden />
          {t('chat.clarification.hintHeading', { defaultValue: 'AI hint' })}
        </span>
      )}
      {ready ? (
        <>
          {ready.clarifying_question && !ready.suggested_option_id && (
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#2d2a36', lineHeight: 1.5 }}>{ready.clarifying_question}</p>
          )}
          <div style={{ overflowX: 'auto', maxWidth: '100%', border: '1px solid #e3e1ea', borderRadius: 8 }} tabIndex={0}>
            <table aria-label={t('chat.clarification.comparisonTitle', { defaultValue: 'Compare the parts' })}
              style={{ borderCollapse: 'collapse', width: '100%', minWidth: candidates.length > 1 ? 120 + candidates.length * 210 : 300, fontSize: 13, lineHeight: 1.5 }}>
              <thead>
                <tr style={{ background: '#f3f1ff' }}>
                  <th scope="col" style={{ padding: 12, textAlign: 'start', minWidth: 120 }}>{t('chat.clarification.comparisonDifference', { defaultValue: 'What differs' })}</th>
                  {candidates.map(c => <th scope="col" key={c.option_id} style={{ padding: 12, textAlign: 'start', minWidth: 210 }}>
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                      <RefChip t={t} question={question} optionId={c.option_id} onFocus={onFocusOption} />
                      {distinguishingTags(c).length > 0 && (
                        <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {distinguishingTags(c).map((tag, index) => (
                            <span key={`${tag}-${index}`} style={{ fontSize: 10.5, fontWeight: 600, color: AMBER_FG, background: AMBER_BG, border: `1px solid ${AMBER_BORDER}`, borderRadius: 5, padding: '2px 6px' }}>
                              {tag}
                            </span>
                          ))}
                        </span>
                      )}
                    </span>
                  </th>)}
                </tr>
              </thead>
              <tbody>
                {comparisonFactors.map((f, i) => (
                  <tr key={i}>
                    <th scope="row" style={{ padding: 12, textAlign: 'start', borderTop: '1px solid #e3e1ea' }}>{f.title}</th>
                    {candidates.map(c => <td key={c.option_id} style={{ padding: 12, verticalAlign: 'top', borderTop: '1px solid #e3e1ea' }}>
                      {f.per_option?.find(p => p.option_id === c.option_id)?.text || ''}
                    </td>)}
                  </tr>
                ))}
                {candidates.some(c => c.summary) && (
                  <tr>
                    <th scope="row" style={{ padding: 12, textAlign: 'start', borderTop: '1px solid #e3e1ea' }}>{t('chat.clarification.comparisonLookFor', { defaultValue: 'Choose this when' })}</th>
                    {candidates.map(c => <td key={c.option_id} style={{ padding: 12, verticalAlign: 'top', borderTop: '1px solid #e3e1ea', color: '#625f6c' }}>
                      {conciseChoiceText(c.summary || '') || ''}
                    </td>)}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {identical.length > 0 && (
            <div data-testid="comparison-identical" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#3f3b56', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {t('chat.clarification.comparisonIdentical', { defaultValue: "What's identical" })}
              </span>
              <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {identical.map((d, i) => (
                  <span key={`${d.label}-${i}`} data-testid="comparison-identical-item" style={{ fontSize: 11.5, color: '#3f3b56', background: '#f6f4f0', border: '1px solid #e3e1ea', borderRadius: 6, padding: '2px 7px', lineHeight: 1.45, overflowWrap: 'anywhere' }}>
                    <span style={{ fontWeight: 500 }}>{d.label}: </span><strong style={{ fontWeight: 700 }}>{d.value}</strong>
                  </span>
                ))}
              </span>
            </div>
          )}
          <p style={{ margin: '6px 0 0', fontSize: 11, color: '#6c6677', lineHeight: 1.5 }}>
            {t('chat.clarification.hintDisclaimer', { defaultValue: 'AI-generated guidance based on available details. It may be wrong; verify the part before choosing.' })}
          </p>
          {ready.suggested_option_id && (
            <p style={{ margin: 0, fontSize: '12px', color: AMBER_FG, fontWeight: 600, display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
              {t('chat.clarification.hintSuggested', { defaultValue: 'Leaning towards: {{label}}', label: '' }).replace(/\s*$/, '')}
              <RefChip t={t} question={question} optionId={ready.suggested_option_id} onFocus={onFocusOption} />
            </p>
          )}
        </>
      ) : (
        <p style={{ margin: 0, fontSize: '12.5px', color: AMBER_FG }}>
          {t('chat.clarification.hintError', { defaultValue: 'Could not get a hint right now. Please try again.' })}
        </p>
      )}
    </div>
  );
};

/** Speech bubble holding the hint, anchored under the row that carries the
 *  Hint pill (the parent must be `position: relative`). Overlays whatever is
 *  below instead of pushing it down; the tail points up at the pill on the
 *  right. Shows a spinner while the request is in flight, the note once it
 *  lands; Escape, an outside click, or ✕ close it. */
export const HintBubble: React.FC<{
  t: T;
  question: ClarificationQuestion;
  state: ClarificationHintState;
  onClose: () => void;
  /** `below` (default) hangs it under the row that carries the pill. `overlay`
   *  floats as a compact panel over the diagram while leaving the parts list
   *  visible; long comparisons scroll inside the panel. */
  placement?: 'below' | 'overlay';
  /** Vertical position inside the diagram pane, derived from the button that
   *  opened this bubble. */
  anchorTop?: number;
  /** Jump to a candidate named in the note without closing the comparison. */
  onFocusOption?: FocusOption;
  /** Heading (and dialog name); default "AI hint". */
  title?: string;
  /** Also name what the candidates have in common. */
  showIdentical?: boolean;
}> = ({ t, question, state, onClose, placement = 'below', anchorTop, onFocusOption, title, showIdentical }) => {
  const heading = title || t('chat.clarification.hintHeading', { defaultValue: 'AI hint' });
  const ref = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    window.addEventListener('keydown', onKey, true);
    // Deferred so the click that opened the bubble doesn't immediately close it.
    const id = window.setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.clearTimeout(id);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);
  return (
    <div
      ref={ref}
      role="dialog"
      aria-label={heading}
      style={{
        position: 'absolute', zIndex: 20,
        display: 'flex', flexDirection: 'column',
        background: '#ffffff', border: `1px solid ${AMBER_BORDER}`, borderRadius: 12,
        boxShadow: '0 12px 32px rgba(32,30,26,0.18)',
        ...(placement === 'overlay'
          ? {
              top: anchorTop ?? 12,
              right: 8,
              width: 'calc(100% - 32px)',
              maxWidth: 1040,
              maxHeight: `min(680px, calc(100% - ${(anchorTop ?? 12) + 8}px))`,
            }
          : { top: 'calc(100% + 10px)', left: 0, right: 0, maxHeight: 'min(60vh, 520px)' }),
      }}
    >
      {/* Tail — a rotated square peeking out of the top edge, under the pill.
          An overlay has nothing to point at: it covers the surface instead. */}
      {placement === 'below' && (
        <span
          aria-hidden
          style={{
            position: 'absolute', top: -7, right: 28, width: 12, height: 12,
            background: '#ffffff', borderLeft: `1px solid ${AMBER_BORDER}`, borderTop: `1px solid ${AMBER_BORDER}`,
            transform: 'rotate(45deg)',
          }}
        />
      )}
      {placement === 'overlay' && anchorTop !== undefined && (
        <span
          aria-hidden
          style={{
            position: 'absolute', top: 12, right: -7, width: 12, height: 12,
            background: '#ffffff', borderRight: `1px solid ${AMBER_BORDER}`, borderTop: `1px solid ${AMBER_BORDER}`,
            transform: 'rotate(45deg)',
          }}
        />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px 10px 16px', borderBottom: '0.5px solid rgba(32,30,26,0.10)', borderRadius: '12px 12px 0 0' }}>
        <Sparkles size={15} strokeWidth={2.2} color={AMBER_FG} aria-hidden />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: AMBER_FG, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
          {heading}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('chat.clarification.close', { defaultValue: 'Close' })}
          style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 3, borderRadius: 6, color: '#696682', display: 'inline-flex' }}
        >
          <X size={14} strokeWidth={2.2} />
        </button>
      </div>
      <div style={{ padding: '14px 16px 16px', overflowY: 'auto', minHeight: 0 }}>
        {state.status === 'loading' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#696682', padding: '4px 0' }}>
            <Loader2 size={15} strokeWidth={2.2} className="animate-spin" aria-hidden />
            {t('chat.clarification.hintLoading', { defaultValue: 'Thinking…' })}
          </div>
        ) : (
          <>
            <HintNote t={t} question={question} state={state} bare onFocusOption={onFocusOption} showIdentical={showIdentical} />
            {/* The tags land first; the comparison rows are still being written
                (the poll swaps them in). Say so instead of showing a thin box. */}
            {state.status === 'ready' && state.hint.refining && !(state.hint.factors || []).length && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#696682', padding: '8px 0 2px' }}>
                <Loader2 size={15} strokeWidth={2.2} className="animate-spin" aria-hidden />
                {t('chat.clarification.hintComparing', { defaultValue: 'Comparing the rest…' })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
